import { styled, keyframes, css } from "styled-components";

import { Box, Flex } from "../../ppui/layout";
import { theme } from "../../theme";
import { lighten, rgba } from "polished";

// Define keyframes for the background fade-in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Styled components
export const TableAction = styled(Flex)(() => ({
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1.25rem",
}));
export const ActionLeft = styled(Flex)(() => ({
  alignItems: "center",
  gap: "0.625rem",
}));
export const ActionRight = styled(Flex)(() => ({
  alignItems: "center",
  gap: "0.625rem",
  flexGrow: 1,
  flexBasis: 0,
  justifyContent: "flex-end",
}));
export const TablePagination = styled(Flex)(() => ({
  alignItems: "center",
  justifyContent: "space-between",
  marginBlockStart: "auto",
  padding: "1.25rem",
}));
export const PaginationLeft = styled(Flex)(() => ({
  alignItems: "center",
  gap: "0.625rem",
}));
export const PaginationRight = styled(Flex)(() => ({
  alignItems: "center",
  gap: "0.625rem",
  flexGrow: 1,
  flexBasis: 0,
  justifyContent: "flex-end",
}));
export const TableContainer = styled(Box)((maxHeight) => ({
  height:maxHeight? "calc(100vh - 16.75rem)" : "calc(100vh - 20.5rem)",
  overflow: "auto",
}));
export const TableHead = styled("thead")(() => ({}));
export const TableBody = styled("tbody")(() => ({}));
export const TableRow = styled("tr")(() => ({
  "&:hover": {
    td: {
      backgroundColor: lighten(0.37, theme.colors.default.light),
      "& .avatarHover": {
        backgroundColor: rgba(theme.colors.secondary.light, 0.35),
      },
    },
  },
}));

export const Table = styled("table")(() => ({
  width: "100%",
  borderCollapse: "collapse",
  border: "none",
  fontFamily: theme.typography.fontFamily,
  "@media (max-width: 600px)": {},
  "@media (min-width: 601px) and (max-width: 1200px)": {},
}));

// Styled component for `td`
const StyledTd = styled("td")((props) => {
  const { align } = props;
  return {
    padding: "0.75rem 1.25rem",
    borderBlockEnd: `${theme.colors.default.main} solid 0.03125rem`,
    textAlign: align || "left",
    whiteSpace: "nowrap",
    fontSize: "0.75rem",
    color: theme.colors.dark.main,
    // '&:nth-child(1)':{padding: "0.5rem 1.25rem 0.5rem",},
    // '&:nth-last-child(1)':{padding: "0.5rem 1.25rem 0.5rem",},
    [`& a`]: { fontSize: "0.75rem" },
    "&.columnSticky": {
      position: "sticky",
      left: 0,
      backgroundColor: theme.colors.light.light,
      "&:after":{
        content:`""`,
        position:"absolute",
        width:"100%",
        height:'0.03125rem',
        backgroundColor:theme.colors.default.main,
        zIndex:1,
        top:'0',
        left:0,
      }
    },
  };
});

// Styled component for `th` with extra styles
const StyledTh = styled(StyledTd)(() => {
  return {
    fontWeight: 500,
    backgroundColor: lighten(0.1, theme.colors.default.main),
    "&.stickyHeader": { position: "sticky", top: 0, zIndex: 2 },
    "&.columnSticky": {
      backgroundColor: lighten(0.1, theme.colors.default.main),
      zIndex: 3,
    },
  };
});

// TableCell component that switches between `th` or `td` based on the `header` prop
export const TableCell = ({ isHeader = false, ...props }) => {
  const Cell = isHeader ? StyledTh : StyledTd;
  return <Cell {...props} />;
};

export const ColumnManagement = styled(Box)(() => {
  return {
    position: "fixed",
    width: "100%",
    height: "100%",
    left: 0,
    top: 0,
    zIndex: 20,
  };
});

// Updated ColumnManageBg with animation wrapped in css helper
export const ColumnManageBg = styled(Box)(({ theme }) => {
  return css`
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 20;
    background-color: ${rgba(theme.colors.dark.dark, 0.75)};
    animation: ${fadeIn} 0.3s ease-out;
  `;
});

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -25%) ;
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) ;
  }
`;
export const ColumnManageBox = styled(Box)(() => {
  return css`
    z-index: 21;
    background-color: ${theme.colors.light.light};
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 50rem;
    border-radius: ${theme.radius.card};
    padding: 1.25rem;
    animation: ${slideIn} 0.2s ease-out;
    
  `;
});

export const ColumnHeader = styled(Flex)(() => {
  return {
    paddingBlockEnd: "1.25rem",
    fontSize: "1.25rem",
    fontWeight: 500,
    backgroundColor: theme.colors.light.light,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };
});
export const ColumnAction = styled(Flex)(() => {
  return {
    paddingBlockStart: "1.25rem",
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.625rem",
  };
});

export const ColumnList = styled("div")(() => {
  return {
    width: "100%",
    maxHeight: "60vh",
    overflowY: "auto",
    paddingInlineEnd: "1.25rem",
    "& ul": {
      padding: 0,
      margin: 0,
      listStyle: "none",
      display: "flex",
      gap: "0.9375rem",
      flexWrap: "wrap",
    },
    "& li": {
      borderRadius: theme.radius.list,
      backgroundColor: theme.colors.background.main,
      width: `calc((100% - 1.875rem)/3)`,
      "&:hover": { backgroundColor: theme.colors.default.main },
      "&>div label": { padding: "0.75rem 1rem" },
    },
  };
});

export const ColumnSelected = styled("div")(() => {
  return {
    display:"none",
    width: "30%",
    maxHeight: "60vh",
    overflowY: "auto",
    paddingInlineStart: "1.25rem",
    borderInlineStart: `solid ${theme.colors.default.main} 0.0625rem`,
    "& ul": {
      padding: 0,
      margin: 0,
      listStyle: "none",
      display: "flex",
      flexDirection: "column",
      gap: "0.9375rem",
    },
    "& li": {
      borderRadius: `calc(${theme.radius.element}/2)`,
      backgroundColor: theme.colors.light.main,
      padding: "0.75rem 1rem",
      display: "flex",
      alignItems: "center",
      "&:hover": { backgroundColor: theme.colors.light.dark },
    },
  };
});
